<script setup>
import { BButton, BCol, BLink, BRow, BTable } from 'bootstrap-vue-next';
import { ref } from 'vue';

import { fetchRewards } from '~/api/rewards';
import ErrorState from '~/common/components/ErrorState.vue';
import IssueRewardModal from '~/common/components/IssueRewardModal.vue';
import RoutePagination from '~/common/components/RoutePagination.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import UserWithGitLabProfileLink from '~/common/components/UserWithGitLabProfileLink.vue';
import { REWARD_REASONS } from '~/common/Constants';
import { dateFieldFormatter } from '~/common/FieldFormats';
import { useVueData } from '~/common/useVueData';

import ActionCard from './home-page/ActionCard.vue';

const { isAdmin: admin } = useVueData();

const rewards = ref([]);
const filterData = ref({
  currentPage: 1,
});
const pageData = ref({
  perPage: 50,
  totalRows: 0,
});
const busy = ref(true);
const error = ref(null);
const showIssueRewardModal = ref(false);

const giftCodeField = admin
  ? [{
      key: 'issue_web_url',
      label: 'Reward issue',
    }]
  : [];
const fields = [
  {
    key: 'username',
    label: 'Username',
  },
  {
    key: 'reason',
    label: 'Reason',
    formatter: ({ value }) => REWARD_REASONS[value],
  },
  ...giftCodeField,
  {
    key: 'credits',
    label: 'Credits',
  },
  {
    key: 'awarded_by',
    label: 'Issued by',
  },
  {
    key: 'created_at',
    label: 'Issued date',
    formatter: dateFieldFormatter,
  },
];

const fetchData = async (page) => {
  busy.value = true;
  error.value = null;

  try {
    const result = await fetchRewards(page);
    rewards.value = result.records;
    pageData.value = result.pageData;
  } catch (e) {
    error.value = e;
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <ErrorState v-if="error" />
  <ThemedOverlay
    v-else
    :show="busy"
  >
    <BButton
      v-if="admin"
      variant="outline-primary"
      data-test-id="issue-rewards-button"
      @click="showIssueRewardModal = true"
    >
      Issue rewards
    </BButton>
    <BRow class="mt-3">
      <BCol
        md="6"
        class="mb-3"
      >
        <ActionCard
          title="Reward store"
          action-link="https://rewards.gitlab.com/"
          action-name="Browse rewards"
          class="h-100"
        >
          Earn credits by contributing, levelling up, and supporting the community.
          Redeem your credits for rewards you'll love.
        </ActionCard>
      </BCol>
      <BCol class="mb-3">
        <ActionCard
          title="Tree Nation"
          action-link="https://tree-nation.com/profile/gitlab"
          action-name="Visit our forest"
          class="h-100"
        >
          Use your reward credits to plant trees in the GitLab Forest.
          See the trees already planted by the contributor community.
        </ActionCard>
      </BCol>
    </BRow>
    <BTable
      :fields="fields"
      :items="rewards"
      responsive="md"
      show-empty
    >
      <template #cell(username)="{ value }">
        <UserWithGitLabProfileLink
          :username="value"
          :filter-data="filterData"
        />
      </template>
      <template #cell(issue_web_url)="{ value }">
        <BLink
          is-unsafe-link
          :href="value"
          target="_blank"
        >
          #{{ value.split('/').pop() }}
        </BLink>
      </template>
      <template #cell(awarded_by)="cell">
        <BLink
          is-unsafe-link
          :href="cell.item.awarded_by_web_url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
      </template>
    </BTable>
    <RoutePagination
      :total-rows="pageData.totalRows"
      :per-page="pageData.perPage"
      @update:model-value="fetchData($event)"
    />
  </ThemedOverlay>
  <IssueRewardModal
    v-if="admin"
    v-model="showIssueRewardModal"
  />
</template>
